import React, { memo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Paper, Typography, Box, alpha, Chip, LinearProgress } from '@mui/material';
import { Loop, Repeat } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const LoopNode = memo(({ data, selected, id }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Use theme primary colors for loop nodes
  const nodeColor = data.color || theme.palette.primary.main;
  const textColor = theme.palette.getContrastText(nodeColor);
  const borderColor = selected ? theme.palette.secondary.main : 'transparent';
  const hoverColor = alpha(nodeColor, 0.9);

  // Loop status and progress
  const currentIteration = data.currentIteration || 0;
  const maxIterations = data.maxIterations || data.iterationCount || 1;
  const loopProgress = maxIterations > 0 ? (currentIteration / maxIterations) * 100 : 0;
  const isInfiniteLoop = data.isInfinite || maxIterations === -1;

  const handleDoubleClick = useCallback((event) => {
    event.stopPropagation();
    const editEvent = new CustomEvent('editNode', { detail: { nodeId: id } });
    window.dispatchEvent(editEvent);
  }, [id]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const contextEvent = new CustomEvent('nodeContextMenu', {
      detail: {
        nodeId: id,
        x: event.clientX,
        y: event.clientY
      }
    });
    window.dispatchEvent(contextEvent);
  }, [id]);

  const getLoopIcon = () => {
    return data.loopType === 'repeat' ? <Repeat /> : <Loop />;
  };

  const getLoopGradient = () => {
    if (data.status === 'running') {
      return `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`;
    } else if (data.status === 'completed') {
      return `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.light} 100%)`;
    } else if (data.status === 'error') {
      return `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.light} 100%)`;
    }
    return `linear-gradient(135deg, ${nodeColor} 0%, ${alpha(nodeColor, 0.8)} 100%)`;
  };

  return (
    <Paper
      elevation={selected ? 12 : 4}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      sx={{
        padding: 2.5,
        borderRadius: 3,
        minWidth: 200,
        minHeight: 120,
        position: 'relative',
        overflow: 'visible',
        cursor: 'move',
        background: isDark
          ? `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.default, 0.8)})`
          : `linear-gradient(145deg, ${alpha('#ffffff', 0.95)}, ${alpha('#f5f5f5', 0.9)})`,
        backdropFilter: 'blur(10px)',
        border: selected
          ? `2px solid ${theme.palette.primary.main}`
          : `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        transition: theme.transitions.create([
          'transform', 'box-shadow', 'border'
        ], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
          transform: 'translateY(-4px) scale(1.02)',
          boxShadow: theme.shadows[16],
          border: `1px solid ${alpha(nodeColor, 0.3)}`,
          '& .node-header': {
            background: getLoopGradient(),
          },
          '& .loop-icon': {
            transform: 'rotate(360deg)',
          }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: 'inherit',
          background: `radial-gradient(circle at top right, ${alpha(nodeColor, 0.1)}, transparent)`,
          pointerEvents: 'none',
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Loop node: ${data.label || 'Loop'}`}
      aria-selected={selected}
    >
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: getLoopGradient(),
          border: `2px solid ${theme.palette.background.paper}`,
          width: 14,
          height: 14,
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
        }}
      />

      {/* Loop Header */}
      <Box
        className="node-header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          p: 1.5,
          mx: -1,
          mt: -1,
          borderRadius: 2,
          background: alpha(nodeColor, 0.08),
          backdropFilter: 'blur(8px)',
          transition: 'background 0.3s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            p: 0.5,
            borderRadius: 1.5,
            background: getLoopGradient(),
            display: 'flex',
            color: 'white',
            boxShadow: `0 2px 8px ${alpha(nodeColor, 0.3)}`,
          }}>
            <Box
              className="loop-icon"
              sx={{
                display: 'flex',
                transition: 'transform 0.5s ease',
                animation: data.status === 'running' ? 'spin 2s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                }
              }}
            >
              {getLoopIcon()}
            </Box>
          </Box>
          <Typography variant="subtitle1" fontWeight="600">
            {data.label || 'Loop'}
          </Typography>
        </Box>

        {/* Loop Type Badge */}
        <Chip
          label={isInfiniteLoop ? '∞' : `${currentIteration}/${maxIterations}`}
          size="small"
          sx={{
            backgroundColor: alpha(nodeColor, 0.2),
            color: nodeColor,
            fontWeight: 'bold',
            fontSize: '0.7rem',
          }}
        />
      </Box>

      {/* Loop Condition/Description */}
      {data.condition && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
            fontStyle: 'italic',
          }}
        >
          Condition: {data.condition}
        </Typography>
      )}

      {/* Progress Bar for Finite Loops */}
      {!isInfiniteLoop && maxIterations > 1 && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="caption" sx={{ color: nodeColor }} fontWeight="600">
              {Math.round(loopProgress)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={loopProgress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: alpha(nodeColor, 0.1),
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: getLoopGradient(),
              }
            }}
          />
        </Box>
      )}

      {/* Loop Metadata */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
        {data.loopType && (
          <Chip
            label={data.loopType.toUpperCase()}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              backgroundColor: alpha(nodeColor, 0.1),
              color: nodeColor,
              border: `1px solid ${alpha(nodeColor, 0.3)}`,
            }}
          />
        )}

        {data.breakCondition && (
          <Chip
            label="BREAKABLE"
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.palette.warning.main, 0.1),
              color: theme.palette.warning.dark,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            }}
          />
        )}
      </Box>

      {/* Loop Status Indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: -8,
          right: -8,
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: getLoopGradient(),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.2)}`,
          border: `2px solid ${theme.palette.background.paper}`,
          animation: data.status === 'running' ? 'pulse 2s infinite' : 'none',
          '@keyframes pulse': {
            '0%': {
              boxShadow: `0 4px 12px ${alpha(nodeColor, 0.4)}`,
            },
            '50%': {
              boxShadow: `0 4px 20px ${alpha(nodeColor, 0.6)}`,
            },
            '100%': {
              boxShadow: `0 4px 12px ${alpha(nodeColor, 0.4)}`,
            },
          }
        }}
      >
        {isInfiniteLoop ? '∞' : currentIteration}
      </Box>

      {/* Loop Back Handle (left side for loop back connection) */}
      <Handle
        type="source"
        position={Position.Left}
        id="loop-back"
        style={{
          background: alpha(theme.palette.warning.main, 0.8),
          border: `2px solid ${theme.palette.background.paper}`,
          width: 14,
          height: 14,
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
        }}
      />

      {/* Continue/Exit Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="continue"
        style={{
          background: getLoopGradient(),
          border: `2px solid ${theme.palette.background.paper}`,
          width: 14,
          height: 14,
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
        }}
      />

      {/* Loop Back Arrow Label */}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          left: -45,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '0.6rem',
          color: theme.palette.warning.main,
          fontWeight: 'bold',
          opacity: selected ? 1 : 0.7,
          transition: theme.transitions.create('opacity'),
        }}
      >
        LOOP ↶
      </Typography>
    </Paper>
  );
});

LoopNode.displayName = 'LoopNode';

export default LoopNode;